import { FC, useEffect, useRef, useState } from 'react';
import { useLocation, useMeasure } from 'react-use';

import Icon from 'atoms/Icons/Icon';
import color from 'atoms/colors/colors';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image from 'quarks/Image';
import Link from 'quarks/Link';
import Text from 'quarks/Text';

import Pagination from 'molecules/Pagination/NumberedPagination';

import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import ComponentButton from 'components/Button/ComponentButton';
import WebinarCard from 'components/Cards/WebinarCard';
import useResourcePosts from 'components/Resources/utils/useResourcePosts';
import FilterSection from 'components/Webinar/FilterSection';

import richTextParser from 'utils/richTextParser';
import useSwipeable from 'utils/useSwipeable';

import type { ContentfulComponentSingleInstance, ContentfulTemplateWebinar } from 'graphqlTypes';

type WebinarLandingProps = ContentfulComponentSingleInstance & {
  backgroundIsDark?: boolean;
  pageType?: string;
};

const scrollPosition = 'scroll-margin-top: 150px;';

interface FilterOptions {
  selectedTopics: string[];
  selectedIndustries: string[];
}

const WebinarLandingPage: FC<WebinarLandingProps> = ({ heading, body }) => {
  const { webinars } = useResourcePosts();

  const posts: ContentfulTemplateWebinar[] = webinars?.nodes || [];
  const { pathname } = useLocation();
  const upComingFilter = (x: ContentfulTemplateWebinar) => new Date(x.date).getTime() > new Date().getTime();
  const onDemandFilter = (x: ContentfulTemplateWebinar) => new Date(x.date).getTime() < new Date().getTime();
  const [filtered, setFiltered] = useState(posts.filter(onDemandFilter));
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    selectedIndustries: [],
    selectedTopics: [],
  });

  const [activeIndex, setActive, cardContainerRef] = useSwipeable(filtered.length);
  const [ref, { width: cardWidth }] = useMeasure<HTMLDivElement>();

  const [page, setPage] = useState(1);

  const upcoming = posts?.filter(upComingFilter).reverse();
  const featuredUpcoming = upcoming?.[0];
  const upcomingCards = upcoming?.slice(1);
  const hasOnDemandCards = filtered?.length > 0;
  const wrapper = useRef(null);

  const perCount = 12;

  const urlParams = pathname?.split('/') || [];
  const currentPage = pathname?.includes('/page/') ? urlParams[urlParams.length - 1] : 1;
  const baseUrl = '/webinar';

  useEffect(() => {
    if (filterOptions.selectedIndustries.length > 0 || filterOptions.selectedTopics.length > 0) {
      const data = posts
        .filter(onDemandFilter)
        .filter(
          value =>
            value?.seo?.taxonomy &&
            value?.seo?.taxonomy?.industries?.some(
              industry =>
                (filterOptions.selectedIndustries.length === 0 ||
                  filterOptions.selectedIndustries.includes(industry)) &&
                (filterOptions.selectedTopics.length === 0 ||
                  value?.seo?.taxonomy?.topics?.some(topic => filterOptions.selectedTopics.includes(topic))),
            ),
        );
      setFiltered(data);
    } else {
      setFiltered(posts.filter(onDemandFilter));
    }
    setPage(1);
    window.history.pushState({}, '', baseUrl);
  }, [filterOptions]);

  useEffect(() => {
    setPage(Number(currentPage));
  }, [currentPage]);

  const setCurrentPage = (pg: number) => {
    setPage(pg);
    window.history.pushState({}, '', `${baseUrl}/page/${pg}#listing-section`);
    const position = document.querySelector('#listing-section');
    if (position) {
      setTimeout(() => {
        window.scrollTo({
          top: position.getBoundingClientRect().top + window.scrollY - 125,
          behavior: 'smooth',
        });
      }, 200);
    }
  };

  const breadcrumbs = [
    { label: 'Resources', link: '/resources' },
    { label: 'Webinars', link: '/webinar' },
  ];

  const formatDate = dt => {
    const options = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      timeZone: 'America/Los_Angeles',
    };

    return new Date(dt).toLocaleDateString(undefined, options);
  };

  const filterUnique = (values: ContentfulTemplateWebinar[], property: string) => [
    ...new Set(
      values
        .map(value => value.seo?.taxonomy)
        .filter(value => value)
        .flatMap(value => value?.[property])
        .filter(value => value),
    ),
  ];

  const handleFilterItemScroll = () => {
    const elem = document.querySelector('#listing-section');
    const topPosition = elem?.getBoundingClientRect().top + window.scrollY - 150;

    window.scrollTo({
      top: topPosition,
      behavior: 'smooth',
    });
  };

  const filterItemClick = (value: string, type: string) => {
    const filterProp = `selected${type}`;

    if (filterOptions[filterProp]?.includes(value)) {
      setFilterOptions({
        ...filterOptions,
        [filterProp]: filterOptions[filterProp].filter(val => val !== value),
      });

      return;
    }

    handleFilterItemScroll();

    setFilterOptions({
      ...filterOptions,
      [filterProp]: [...filterOptions[filterProp], value],
    });
  };

  const clearFilters = () => {
    setFilterOptions({
      selectedIndustries: [],
      selectedTopics: [],
    });
  };

  const industriesList = filterUnique(posts, 'industries');
  const topicsList = filterUnique(posts, 'topics');

  return (
    <Flex flexDirection="column" id="stop">
      <Flex flexDirection="column">
        <Flex
          position="relative"
          flexDirection="column"
          width="100%"
          maxWidth="100%"
          marginX="auto"
          paddingTop={32}
          paddingBottom={12}
          gap="40px"
          zIndex="1"
          xs={{ paddingTop: 0 }}
          md={{ maxWidth: '100%', gap: '64px', marginBottom: 48 }}
        >
          <Flex marginBottom={24} md={{ marginBottom: 32 }}>
            <Breadcrumbs breadcrumbs={breadcrumbs} isDark={true} />
          </Flex>
          <Flex
            position="relative"
            flexDirection="column"
            justifyContent="center"
            gap="32px"
            width="100%"
            maxWidth="100%"
          >
            {heading && (
              <Heading
                fontSize="h3"
                lineHeight="h3"
                textColor="common-white"
                fontFamily="secondaryFont"
                textAlign="center"
                sm={{ fontSize: 'h2', lineHeight: 'h2' }}
                lg={{ fontSize: 'h1', lineHeight: 'h1' }}
              >
                {heading}
              </Heading>
            )}
            {body && (
              <Flex
                textColor="common-white"
                justifyContent="center"
                fontSize="textMd"
                sm={{ fontSize: 'textMd' }}
                lg={{ fontSize: 'textLg' }}
              >
                {richTextParser(body)}
              </Flex>
            )}
          </Flex>
        </Flex>
        {featuredUpcoming && (
          <Flex
            width="100%"
            maxWidth="100%"
            marginX="auto"
            position="relative"
            paddingX={0}
            marginBottom={40}
            marginTop={8}
            paddingTop={8}
            className="featured"
          >
            <Flex
              flexDirection="column-reverse"
              width="100%"
              backgroundColor="common-white"
              boxShadow="lg"
              lg={{ height: '460px' }}
              md={{ height: '480px', flexDirection: 'row' }}
            >
              <Flex
                position="relative"
                flexDirection="column"
                gap="4"
                md={{ width: '33%' }}
                lg={{ width: '33%' }}
                width="100%"
              >
                <Flex
                  position="relative"
                  height="100%"
                  md={{ width: '500px' }}
                  lg={{ width: '400px' }}
                  width="100%"
                  flexDirection="column"
                  gap=".5rem"
                  paddingAll={20}
                  alignItems="flex-start"
                >
                  <p className="font-bold uppercase text-[#4BB699]">{formatDate(featuredUpcoming.dateRaw)} (PST)</p>
                  <h3 className="text-xl font-bold text-gray-900 md:text-display-xs">{featuredUpcoming.title}</h3>
                  <p className="text-md text-black line-clamp-4">{richTextParser(featuredUpcoming.summary)}</p>
                </Flex>
                <ComponentButton
                  variant="link"
                  href={`/webinar/${featuredUpcoming.slug}`}
                  endIcon="Arrow Right"
                  label="Register Now"
                  marginTop={32}
                  className="left-5 bottom-[7px] md:bottom-1 md:left-3 md:absolute !text-[#373680] w-full"
                  css={{
                    fontSize: '14px !important',
                  }}
                />
              </Flex>
              <Flex width="100%" flexDirection="column" justifyContent="flex-start" gap="4">
                <Image
                  image={featuredUpcoming?.featuredImage as ImageData}
                  alt={featuredUpcoming?.title || ''}
                  width="100%"
                  height="100%"
                  objectFit="cover"
                />
              </Flex>
            </Flex>
          </Flex>
        )}
        <Flex
          width="100vw"
          height="100%"
          maxHeight={featuredUpcoming ? '700px' : '300px'}
          position="absolute"
          backgroundColor="navy-900"
          top="0"
          left="0"
          zIndex="-1"
          md={{ maxHeight: featuredUpcoming ? '800px' : '375px' }}
          lg={{ maxHeight: featuredUpcoming ? '600px' : '375px' }}
        ></Flex>
      </Flex>
      {upcomingCards?.length > 0 && (
        <Flex
          fontSize="textLg"
          lineHeight="h5"
          textColor="gray-900"
          fontWeight="semiBold"
          fontFamily="secondaryFont"
          flexDirection="column"
          overflowX="hidden"
          position="relative"
          css={`
            @media (min-width: 768px) {
              &:after {
                content: ' ';
                position: absolute;
                width: 20px;
                height: 100%;
                right: 0;
                bottom: 40px;
                z-index: 20;
                background: linear-gradient(to right, rgba(255, 255, 255, 0) 0, white 100%);
              }
            }
          `}
        >
          Upcoming Webinars
          <Flex
            gap="40px"
            marginTop={32}
            justifyContent="flex-start"
            transform={`translateX(-${activeIndex * cardWidth + activeIndex * 40}px)`}
            transition="transform 200ms cubic-bezier(0.215, 0.610, 0.355, 1.000)"
            minWidth={`calc(100% * ${upcomingCards.length} + (36px * ${upcomingCards.length - 1}))`}
            md={{ minWidth: `calc(100% * ${upcomingCards.length} + (30px * ${upcomingCards.length - 1}))` }}
            ref={cardContainerRef}
          >
            {upcomingCards?.map((post: ContentfulTemplateWebinar) => (
              <Flex key={post?.id} width="100%" maxWidth="100vw" md={{ maxWidth: '33.333vw' }} ref={ref}>
                <Link href={`/webinar/${post.slug}`} textDecoration="none" width="100%">
                  <Flex
                    width="100%"
                    height="100%"
                    flexDirection="column"
                    overflowX="hidden"
                    overflowY="hidden"
                    border="1px solid"
                    borderColor="gray-200"
                    hover={{ boxShadow: 'lg' }}
                    transition="200ms ease-in-out"
                    backgroundColor="common-white"
                    paddingY={20}
                    paddingX={18}
                    borderRadius="4px"
                    boxShadow="md"
                    gap="12px"
                  >
                    <Heading
                      as="h4"
                      textStyle="h6"
                      line-height="textXxl"
                      data-cy="title"
                      textColor="gray-900"
                      fontWeight="semiBold"
                    >
                      {formatDate(post.dateRaw)} (PST)
                    </Heading>
                    <Text textStyle="textSm" line-height="textXxl" height="38px" textColor="gray-900">
                      {post?.title}
                    </Text>
                    <ComponentButton
                      variant="link"
                      href={`/webinar/${post.slug}`}
                      endIcon="Arrow Right"
                      label="Register Now"
                      className="text-[#373680] mt-[40px]"
                      css={{
                        fontSize: '14px !important',
                      }}
                    />
                  </Flex>
                </Link>
              </Flex>
            ))}
          </Flex>
          {upcomingCards?.length > 1 && (
            <Flex gap="12px" justifyContent="flex-end" marginTop={24}>
              <Flex
                borderRadius="6px"
                backgroundColor="navy-50"
                width="36px"
                height="36px"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                onClick={() => (activeIndex === 0 ? setActive(upcomingCards.length - 1) : setActive(activeIndex - 1))}
              >
                <Icon id="arrow-left" fill={color.gray[900]} size={16} />
              </Flex>
              <Flex
                borderRadius="6px"
                backgroundColor="navy-50"
                width="36px"
                height="36px"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                onClick={() => (activeIndex === upcomingCards.length - 1 ? setActive(0) : setActive(activeIndex + 1))}
              >
                <Icon id="arrow-right" fill={color.gray[900]} size={16} />
              </Flex>
            </Flex>
          )}
        </Flex>
      )}
      {hasOnDemandCards && (
        <>
          <Flex
            id="listing-section"
            css={scrollPosition}
            backgroundColor="common-transparent"
            paddingTop={featuredUpcoming ? 0 : 80}
            marginTop={0}
            gap="40px"
            flexWrap="wrap"
            md={{ flexWrap: 'nowrap', marginTop: 48 }}
          >
            <Flex
              boxShadow="l"
              flexDirection="column"
              md={{ width: '33%' }}
              width="100%"
              maxWidth="100%"
              position="relative"
              ref={wrapper}
            >
              {hasOnDemandCards && industriesList.length + topicsList.length > 0 && (
                <Flex
                  paddingAll={20}
                  backgroundColor="gray-950"
                  justifyContent="flex-start"
                  flexDirection="row"
                  md={{ minHeight: '522px', marginTop: 0 }}
                  borderRadius="4px"
                  gap="32px"
                  flexWrap="wrap"
                  marginTop={48}
                  lg={{ position: 'sticky', top: '120px' }}
                >
                  <FilterSection
                    values={industriesList}
                    selectedValues={filterOptions.selectedIndustries}
                    itemClick={(value: string) => {
                      filterItemClick(value, 'Industries');
                    }}
                    flexDirection="column"
                    gap="20px"
                    width="100%"
                    md={{ minHeight: '35%' }}
                    label="Filter by Industry"
                  />
                  <FilterSection
                    values={topicsList}
                    selectedValues={filterOptions.selectedTopics}
                    itemClick={(value: string) => {
                      filterItemClick(value, 'Topics');
                    }}
                    flexDirection="column"
                    gap="20px"
                    width="100%"
                    md={{ minHeight: '35%' }}
                    label="Filter by Topic"
                  />
                  <Flex width="100%" height="40px" alignSelf="flex-end">
                    {(filterOptions.selectedIndustries.length > 0 || filterOptions.selectedTopics.length > 0) && (
                      <Container
                        borderRadius="8px"
                        textAlign="center"
                        fontSize="textXs"
                        textColor="gray-900"
                        width="100%"
                        border="1px solid #000000"
                        paddingX={10}
                        paddingY={8}
                        fontWeight="semiBold"
                        cursor="pointer"
                        onClick={clearFilters}
                      >
                        Clear All
                      </Container>
                    )}
                  </Flex>
                </Flex>
              )}
            </Flex>
            <Flex
              flexDirection="column"
              md={{ width: '67%' }}
              width="100%"
              gap="40px"
              position="relative"
              ref={wrapper}
            >
              <Flex justifyContent="flex-start" flexWrap="wrap" gap="30px" className="webinar-cards">
                {filtered &&
                  filtered.slice((page - 1) * perCount, page * perCount).map(post => (
                    <Flex key={post?.id} width="100%" maxWidth="100%" md={{ maxWidth: 'calc((100% - 30px) / 2)' }}>
                      <WebinarCard {...post} backgroundIsDark={false} />
                    </Flex>
                  ))}
                {filtered &&
                  filtered.length === 0 &&
                  filterOptions.selectedIndustries.length + filterOptions.selectedTopics.length > 0 && (
                    <Flex width="100%" justifyContent="center" textColor="gray-900">
                      No results found
                    </Flex>
                  )}
              </Flex>
            </Flex>
          </Flex>
          <Flex marginTop={64}>
            {filtered?.length > 12 && (
              <Pagination
                prefix={baseUrl}
                currentPage={page}
                isSearch
                totalPages={Math.ceil(filtered.length / perCount)}
                setCurrentPage={pg => setCurrentPage(Number(pg))}
              />
            )}
          </Flex>
        </>
      )}
    </Flex>
  );
};

export default WebinarLandingPage;
